// Permission matrix (PLAN §11). Enforced by API middleware and mirrored in the UI from this one table.
// Roles are not a ladder: reviewers can review others' uploads and merge; editors can't.

export const ROLES = ['admin', 'editor', 'reviewer', 'viewer'] as const;
export type Role = (typeof ROLES)[number];
export const isRole = (r: unknown): r is Role => ROLES.includes(r as Role);

export const PERMISSIONS = {
  /** View graph, search, chat, reports, Library, help. */
  'content.read': 'View the graph, search, chat and reports',
  /** Upload, create, edit entities and claims, import datasets, review one's own uploads. */
  'content.write': 'Upload, create and edit',
  /** Review others' uploads, verify/reject/dispute, merge and unmerge entities. */
  'content.review': "Review others' uploads and merge entities",
  /** CSV / PDF / DOCX exports (viewers only when an admin allows it). */
  'content.export': 'Export (CSV, PDF, DOCX)',
  /** Users, schema, templates, AI routing, budgets, audit log, erasure. */
  'admin.manage': 'Manage users, schema and AI policy',
} as const;
export type Permission = keyof typeof PERMISSIONS;

const MATRIX: Record<Role, readonly Permission[]> = {
  admin: ['content.read', 'content.write', 'content.review', 'content.export', 'admin.manage'],
  editor: ['content.read', 'content.write', 'content.export'],
  reviewer: ['content.read', 'content.write', 'content.review', 'content.export'],
  viewer: ['content.read'],
};

/** Roles whose export right an admin can switch (admins always export). */
export const EXPORT_POLICY_ROLES = ['editor', 'reviewer', 'viewer'] as const;
export type ExportPolicyRole = (typeof EXPORT_POLICY_ROLES)[number];

/**
 * Org settings that change the matrix: the per-role export policy (PLAN §11 "Export: configurable" for viewers;
 * task 38 lets an admin switch it for editors and reviewers too). Admins always keep export.
 */
export interface PermissionSettings {
  /** Viewers may export ("configurable" in the matrix). Default: no. */
  viewerExport: boolean;
  /** Editors may export. Default: yes. */
  editorExport: boolean;
  /** Reviewers may export. Default: yes. */
  reviewerExport: boolean;
}
export const DEFAULT_PERMISSION_SETTINGS: PermissionSettings = { viewerExport: false, editorExport: true, reviewerExport: true };

export const exportAllowedKey = (role: ExportPolicyRole) => `${role}Export` as const;

export function can(role: Role, permission: Permission, settings: Partial<PermissionSettings> = DEFAULT_PERMISSION_SETTINGS): boolean {
  if (permission === 'content.export' && role !== 'admin') {
    const allowed = settings[exportAllowedKey(role)];
    return typeof allowed === 'boolean' ? allowed : DEFAULT_PERMISSION_SETTINGS[exportAllowedKey(role)];
  }
  return MATRIX[role].includes(permission);
}

export const permissionsOf = (role: Role, settings?: Partial<PermissionSettings>): Permission[] =>
  (Object.keys(PERMISSIONS) as Permission[]).filter(p => can(role, p, settings));

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  viewer: 'Reads everything: graph, search, chat and reports. Exports only if an admin allows it.',
  editor: 'Also uploads, creates and edits, and exports (unless an admin turns exports off for editors).',
  reviewer: "Also reviews others' uploads (verify, reject, dispute) and merges entities.",
  admin: 'Everything, plus users, schema, AI routing, budgets and the audit log.',
};
